import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { investmentAPI, fundAPI, userPreferencesAPI } from '../services/api';
import { Plus, TrendingUp, Settings, Download, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMobile } from '../hooks/useMobile';
import ViewSelector from '../components/investments/ViewSelector';
import DetailedView from '../components/investments/DetailedView';
import KanbanView from '../components/investments/KanbanView';
import InvestmentTableView from '../components/investments/InvestmentTableView';
import PortfolioSummary from '../components/investments/PortfolioSummary';

const Investments = () => {
  const { user } = useAuth();
  const { canCreate = false, canEdit = false, canDelete = false } = usePermissions() || {};
  const isMobile = useMobile();
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [allInvestments, setAllInvestments] = useState([]); // Store unfiltered investments
  const [funds, setFunds] = useState([]);
  const [fundStages, setFundStages] = useState(['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited']);
  const [selectedFund, setSelectedFund] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('list');
  const [fmvDate, setFmvDate] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(83.5);
  const [asOnDate, setAsOnDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [visibleFields, setVisibleFields] = useState([
    'portfolio_company_name',
    'investment_stage',
    'sector',
    'total_investment',
    'current_stake_value',
    'multiple',
    'irr'
  ]);

  const availableFields = [
    { key: 'portfolio_company_name', label: 'Company Name' },
    { key: 'investment_stage', label: 'Investment Stage' },
    { key: 'sector', label: 'Sector' },
    { key: 'investment_currency', label: 'Currency' },
    { key: 'types_of_securities_offered', label: 'Securities Offered' },
    { key: 'total_investment', label: 'Total Investment' },
    { key: 'total_realisation', label: 'Total Realisation' },
    { key: 'current_stake_value', label: 'Current Stake Value' },
    { key: 'current_shareholding', label: '% Shareholding' },
    { key: 'multiple', label: 'Multiple (MOIC)' },
    { key: 'irr', label: 'IRR %' },
    { key: 'created_at', label: 'Created Date' }
  ];

  useEffect(() => {
    fetchData();
    loadUserPreferences();
    loadAllStages();
  }, []);

  useEffect(() => {
    fetchInvestments();
    if (selectedFund) {
      loadFundStages();
    }
  }, [selectedFund, fmvDate, asOnDate]);

  const applyFilters = useCallback(() => {
    console.log('Applying filters:', { 
      selectedStage, 
      currentView, 
      allInvestmentsCount: allInvestments.length 
    });
    
    let filtered = [...allInvestments];

    // Filter by stage (only for List and Detailed views, not Kanban)
    if (selectedStage && currentView !== 'kanban') {
      filtered = filtered.filter(inv => 
        (inv.investment_stage || 'Pipeline') === selectedStage
      );
      console.log('Filtered by stage:', selectedStage, '- Result:', filtered.length, 'investments');
    }

    setInvestments(filtered);
  }, [allInvestments, selectedStage, currentView]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchData = async () => {
    try {
      const fundsRes = await fundAPI.getAll();
      setFunds(fundsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const fetchInvestments = async () => {
    setLoading(true);
    try {
      const response = await investmentAPI.getAll(selectedFund || null);
      const investmentsData = response.data;

      // The "as-on date" field drives FMV/IRR computation AND filtering of
      // investments with no transactions on/before that date (parity with
      // Fund > Investments tab behaviour).
      const refDate = asOnDate || fmvDate || null;

      const investmentsWithMetrics = await Promise.all(
        investmentsData.map(async (inv) => {
          try {
            const params = {};
            if (refDate) params.fmv_date = refDate;

            const metricsResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/investments/${inv.id}/metrics`,
              { params }
            );
            return { ...inv, metrics: metricsResponse.data };
          } catch (error) {
            console.error(`Failed to fetch metrics for ${inv.id}:`, error);
            return { ...inv, metrics: null };
          }
        })
      );

      // If as-on-date is set, hide investments with no transactions on/before that date
      const filteredByAsOn = asOnDate
        ? investmentsWithMetrics.filter(inv => inv.metrics?.has_transactions_as_of !== false)
        : investmentsWithMetrics;

      console.log('Fetched investments:', filteredByAsOn.length, '(asOnDate:', asOnDate || 'none', ')');
      setAllInvestments(filteredByAsOn);
    } catch (error) {
      console.error('Failed to fetch investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllStages = async () => {
    // Load all unique stages from all funds
    try {
      const fundsRes = await fundAPI.getAll();
      const allStages = new Set(['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited']);
      
      // You could enhance this to fetch stages from all funds
      setFundStages(Array.from(allStages));
    } catch (error) {
      console.error('Failed to load stages:', error);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const response = await userPreferencesAPI.get();
      const prefs = response.data;
      setCurrentView(prefs.investments_view_type || 'list');
      if (prefs.investments_visible_fields && prefs.investments_visible_fields.length > 0) {
        setVisibleFields(prefs.investments_visible_fields);
      }
      if (prefs.selectedFund) setSelectedFund(prefs.selectedFund);
      if (prefs.selectedStage) setSelectedStage(prefs.selectedStage);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const loadFundStages = async () => {
    if (!selectedFund) return;
    try {
      const response = await investmentAPI.getFundStages(selectedFund);
      setFundStages(response.data.stages || ['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited']);
    } catch (error) {
      console.error('Failed to load fund stages:', error);
    }
  };

  const handleViewChange = async (view) => {
    setCurrentView(view);
    // Filters will be re-applied automatically via useEffect
    try {
      await userPreferencesAPI.update({ investments_view_type: view });
    } catch (error) {
      console.error('Failed to save view preference:', error);
    }
  };

  const handleFieldsChange = async (fields) => {
    setVisibleFields(fields);
    try {
      await userPreferencesAPI.update({ investments_visible_fields: fields });
    } catch (error) {
      console.error('Failed to save field preferences:', error);
    }
  };

  const handleFundChange = (fundId) => {
    console.log('Fund changed to:', fundId);
    setSelectedFund(fundId);
    // Save to backend asynchronously (don't await to prevent blocking)
    userPreferencesAPI.update({ selectedFund: fundId }).catch((error) => {
      console.error('Failed to save fund preference:', error);
    });
  };

  const handleStageFilterChange = (stage) => {
    console.log('Stage changed to:', stage, '| Current all investments:', allInvestments.length);
    setSelectedStage(stage);
    // Save to backend asynchronously (don't await to prevent blocking)
    userPreferencesAPI.update({ selectedStage: stage }).catch((error) => {
      console.error('Failed to save stage preference:', error);
    });
  };

  const handleStageChange = async (investmentId, newStage) => {
    try {
      await investmentAPI.update(investmentId, { investment_stage: newStage });
      // Refresh investments
      fetchInvestments();
    } catch (error) {
      console.error('Failed to update investment stage:', error);
      alert('Failed to update investment stage');
    }
  };

  const handleExport = async (format) => {
    if (!selectedFund) { alert('Please select a fund to export'); return; }
    setExporting(true);
    try {
      const res = await fundAPI.exportInvestmentsSummary(selectedFund, format, asOnDate || undefined);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'excel' ? 'Investment_Summary.xlsx' : 'Investment_Summary.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
    finally { setExporting(false); }
  };

  // Compute totals for visible investments
  // (Tiles removed - Portfolio Summary above already shows these totals)

  const handleDeleteInvestment = async (investmentId) => {
    if (!window.confirm('Are you sure you want to delete this investment? This action cannot be undone.')) {
      return;
    }
    
    try {
      await investmentAPI.delete(investmentId);
      // Refresh investments after deletion
      fetchInvestments();
      alert('Investment deleted successfully');
    } catch (error) {
      console.error('Failed to delete investment:', error);
      alert('Failed to delete investment. Please try again.');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="investments-title">
            Investments
          </h1>
          <p className="text-gray-600 mt-2">Track portfolio investments and performance</p>
        </div>
        {canCreate && !isMobile && (
          <Link
            to="/investments/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="create-investment-button"
          >
            <Plus size={20} className="mr-2" />
            New Investment
          </Link>
        )}
      </div>

      {/* Portfolio Summary */}
      <PortfolioSummary 
        key={`${selectedFund}-${selectedStage}-${investments.length}`}
        selectedFund={selectedFund}
        selectedStage={selectedStage}
        investments={investments}
        onFmvDateChange={setFmvDate}
        onCurrencyChange={(currency, rate) => {
          setSelectedCurrency(currency);
          setExchangeRate(rate);
        }}
      />

      {/* Filter and View Selector */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between space-y-4 lg:space-y-0 gap-4">
        <div className="flex flex-col md:flex-row gap-4 flex-1">
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Fund</label>
            <select
              value={selectedFund}
              onChange={(e) => handleFundChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Funds</option>
              {funds.map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Stage</label>
            <select
              value={selectedStage}
              onChange={(e) => handleStageFilterChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              disabled={currentView === 'kanban'}
            >
              <option value="">All Stages</option>
              {fundStages.map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
            {currentView === 'kanban' && (
              <p className="text-xs text-gray-500 mt-1">Stage filter disabled in Kanban view</p>
            )}
          </div>

          <div className="w-full md:w-52">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar size={13} className="inline mr-1" />As-on Date
            </label>
            <input
              type="date"
              value={asOnDate}
              onChange={(e) => setAsOnDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              data-testid="as-on-date-filter"
            />
          </div>
        </div>

        <div className="flex items-end gap-2">
          {selectedFund && (
            <div className="relative group">
              <button disabled={exporting} className="inline-flex items-center px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="inv-export-btn">
                <Download size={14} className="mr-1" /> {exporting ? '...' : 'Export'}
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                <button onClick={() => handleExport('excel')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg">Excel</button>
                <button onClick={() => handleExport('pdf')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-b-lg">PDF</button>
              </div>
            </div>
          )}
          <ViewSelector
            currentView={currentView}
            onViewChange={handleViewChange}
            visibleFields={visibleFields}
            onFieldsChange={handleFieldsChange}
            availableFields={availableFields}
          />
        </div>
      </div>

      {/* Render based on selected view */}
      {investments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No investments yet</h3>
          <p className="text-gray-500 mb-6">{isMobile ? 'No investments to display' : 'Create your first investment to get started'}</p>
          {canCreate && !isMobile && (
            <Link
              to="/investments/create"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={20} className="mr-2" />
              New Investment
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* List View - Now Table */}
          {currentView === 'list' && (
            <InvestmentTableView
              investments={investments}
              visibleFields={visibleFields}
              onDelete={handleDeleteInvestment}
              canEdit={canEdit}
              canDelete={canDelete}
              currencyFormat="full"
              selectedCurrency={selectedCurrency}
              exchangeRate={exchangeRate}
            />
          )}

          {/* Detailed View */}
          {currentView === 'detailed' && (
            <DetailedView
              investments={investments}
              visibleFields={visibleFields}
              currency="USD"
            />
          )}

          {/* Kanban View */}
          {currentView === 'kanban' && (
            <KanbanView
              investments={investments}
              stages={fundStages}
              onStageChange={handleStageChange}
              currency="USD"
            />
          )}
        </>
      )}
    </div>
  );
};

export default Investments;
